import { Grid, Typography } from "@mui/material";
import ListEmails from "../../components/tables/ListEmails";
import { useContext, useEffect, useState } from "react";
import {
  getMerchantEffectiveEmailConfig,
  updateMerchantEmailConfig,
} from "../../requests/orderRequests";
import { IEmailTemplate, IEffectiveStatusConfig } from "../../interfaces/order";
import { IAlert } from "../../interfaces/alert";
import { ERROR, ORDER_STATUS } from "../../utils/constants";
import { filterMachineValidTemplates } from "../../utils/emailTemplates";
import GenericAlert from "../../components/GenericAlert";
import Loader from "../../components/Loader";
import { IDynamicObjectReturnBoolean } from "../../interfaces/common";
import ConfirmChangesButtons from "../../components/configuration/ConfirmChangesButtons";
import EmailCustomizationModal from "../../components/modals/EmailCustomizationModal";
import { UserContext } from "../../contexts/userContext";

// DB names from the API use underscores ("in_process"), but ORDER_STATUS and the
// rest of the app use space-separated ids ("in process"). Convert before lookup.
const dbToStatusId = (dbName: string) => dbName.split("_").join(" ");
const statusIdToDb = (statusId: string) => statusId.split(" ").join("_");

function mapConfigToTemplates(
  configs: IEffectiveStatusConfig[],
): IEmailTemplate[] {
  return configs.map((cfg) => {
    const statusId = dbToStatusId(cfg.status);
    const orderStatus = ORDER_STATUS.find(({ id }) => id === statusId);
    return {
      status: statusId,
      disabled: cfg.effectiveDisabled,
      alias: "",
      id: statusId,
      label: orderStatus?.label ?? cfg.label,
      description: orderStatus?.description ?? "",
      type: orderStatus?.type ?? "",
    };
  });
}

function buildEventsPayload(
  toggled: IDynamicObjectReturnBoolean,
): Record<string, { disabled: boolean }> {
  const events: Record<string, { disabled: boolean }> = {};
  for (const [key, val] of Object.entries(toggled)) {
    events[statusIdToDb(key)] = { disabled: val };
  }
  return events;
}

function deriveReadonlyStatuses(configs: IEffectiveStatusConfig[]): string[] {
  return configs
    .filter((cfg) => cfg.restricted || cfg.marketplaceDisabled)
    .map((cfg) => dbToStatusId(cfg.status));
}

const SellerEmailNotifications = () => {
  const { selectedMarketplace } = useContext(UserContext);
  const marketplaceId = Number(selectedMarketplace);

  const [effectiveConfig, setEffectiveConfig] = useState<IEffectiveStatusConfig[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<IEmailTemplate[]>([]);
  const [alert, setAlert] = useState({} as IAlert);
  const [loading, setLoading] = useState(true);
  const [toggledDisabledStatuses, setToggledDisabledStatuses] = useState(
    {} as IDynamicObjectReturnBoolean,
  );
  const [openModal, setOpenModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState({} as IEmailTemplate);
  const [selectedAction, setSelectedAction] = useState("view");

  const fetchConfig = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await getMerchantEffectiveEmailConfig(marketplaceId);
      const configs = response as IEffectiveStatusConfig[];
      setEffectiveConfig(configs);
      setEmailTemplates(filterMachineValidTemplates(mapConfigToTemplates(configs)));
    } catch (error: unknown) {
      setAlert({ typeOfAlert: ERROR, message: String(error), showAlert: true });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDisableStatus = (statusId: string, checked: boolean): void => {
    setToggledDisabledStatuses((prev) => ({
      ...prev,
      [statusId]: !checked,
    }));
  };

  const handleSaveChanges = async (): Promise<void> => {
    try {
      const events = buildEventsPayload(toggledDisabledStatuses);
      await updateMerchantEmailConfig(marketplaceId, events);
      setToggledDisabledStatuses({} as IDynamicObjectReturnBoolean);
      await fetchConfig();
    } catch (error: unknown) {
      setAlert({ typeOfAlert: ERROR, message: String(error), showAlert: true });
    }
  };

  const handleSelectedAction = (action: string, status: IEmailTemplate): void => {
    setSelectedAction(action);
    setSelectedStatus(status);
    setOpenModal(true);
  };

  useEffect(() => {
    if (marketplaceId) fetchConfig();
  }, [marketplaceId]);

  const readonlyStatuses = deriveReadonlyStatuses(effectiveConfig);
  const hasPendingChanges = Object.keys(toggledDisabledStatuses).length > 0;

  return (
    <>
      <Typography fontSize={20} mb={2}>
        Notificaciones de email
      </Typography>
      <Typography fontSize={14} mb={3.5}>
        Activa o desactiva las notificaciones por correo para cada estado del
        envio. Los estados bloqueados estan configurados por el Marketplace.
      </Typography>
      {alert.showAlert && (
        <Grid item xs={12} mb={3}>
          <GenericAlert alert={alert} />
        </Grid>
      )}
      <Grid item xs={12} lg={8}>
        {loading ? (
          <Loader size="md" />
        ) : (
          <ListEmails
            emailTemplates={emailTemplates}
            handleSelectedAction={handleSelectedAction}
            toggledDisabledStatuses={toggledDisabledStatuses}
            handleToggleDisableStatus={handleToggleDisableStatus}
            readonlyStatuses={readonlyStatuses}
            isEditRestricted={true} // Este booleano se utiliza para renderizar o el icono que abre el modal para editar un template. Por definicion un seller no tiene ese permiso actualmente, por eso true es un campo fijo
          />
        )}
        {hasPendingChanges && (
          <ConfirmChangesButtons
            confirmButtonText="Guardar cambios"
            handleCancel={() =>
              setToggledDisabledStatuses({} as IDynamicObjectReturnBoolean)
            }
            handleConfirm={handleSaveChanges}
            size={2.5}
          />
        )}
      </Grid>

      <EmailCustomizationModal
        open={openModal}
        setOpen={setOpenModal}
        action={selectedAction}
        status={selectedStatus}
        emailTemplates={emailTemplates}
        fetchEmailTemplates={fetchConfig}
      />
    </>
  );
};

export default SellerEmailNotifications;
